import { create } from 'zustand';
import { 
  getSensors, 
  createSensor, 
  updateSensor, 
  deleteSensor,
  getSensor,
  getSensorReadings,
  submitSensorReading,
  calibrateSensor,
  getSensorHealth,
  getSensorBatteryStatus
} from '../services/sensor.service';
import { 
  SensorSchema, 
  SensorRequest, 
  UpdateSensorRequest, 
  SensorQueryParams,
  PaginatedSensorResponse,
  SensorReading 
} from '../types/sensor.type';
import { AxiosError } from 'axios';

interface SensorState {
  sensors: SensorSchema[];
  selectedSensor: SensorSchema | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  showSensorModal: boolean;
  setShowSensorModal: (show: boolean) => void;
  getSensors: (organizationId: string, params: SensorQueryParams) => Promise<PaginatedSensorResponse | null>;
  getSensor: (sensorId: string) => Promise<SensorSchema | null>;
  createSensor: (sensor: SensorRequest) => Promise<SensorSchema | null>;
  updateSensor: (sensorId: string, sensor: UpdateSensorRequest) => Promise<SensorSchema | null>;
  deleteSensor: (sensorId: string) => Promise<boolean>;
  getSensorReadings: (sensorId: string, params: { start_date?: string; end_date?: string }) => Promise<SensorReading[] | null>;
  submitSensorReading: (sensorId: string, reading: SensorReading) => Promise<boolean>;
  calibrateSensor: (sensorId: string) => Promise<boolean>;
  getSensorHealth: (sensorId: string) => Promise<{ status: string; details: any } | null>;
  getSensorBatteryStatus: (sensorId: string) => Promise<{ level: number; status: string } | null>;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

export const useSensorStore = create<SensorState>((set) => ({
  sensors: [],
  selectedSensor: null,
  total: 0,
  isLoading: false,
  error: null,
  showSensorModal: false,
  setShowSensorModal: (show: boolean) => set({ showSensorModal: show }),

  getSensors: async (organizationId, params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getSensors(organizationId, params);
      const data = response as GenericResponse<PaginatedSensorResponse>;
      
      set({ 
        sensors: data.result.sensors,
        total: data.result.total,
        isLoading: false 
      });

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get sensors', isLoading: false });
      } else {
        set({ error: 'Failed to get sensors', isLoading: false });
      }
      return null;
    }
  },

  getSensor: async (sensorId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getSensor(sensorId);
      const data = response as GenericResponse<SensorSchema>;
      
      set({ 
        selectedSensor: data.result,
        isLoading: false 
      });

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get sensor', isLoading: false });
      } else {
        set({ error: 'Failed to get sensor', isLoading: false });
      }
      return null;
    }
  },

  createSensor: async (sensor) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createSensor(sensor);
      const data = response as GenericResponse<SensorSchema>;
      
      set((state) => ({ 
        sensors: [...state.sensors, data.result],
        isLoading: false 
      }));

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to create sensor', isLoading: false });
      } else {
        set({ error: 'Failed to create sensor', isLoading: false });
      }
      return null;
    }
  },

  updateSensor: async (sensorId, sensor) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateSensor(sensorId, sensor);
      const data = response as GenericResponse<SensorSchema>;
      
      set((state) => ({ 
        sensors: state.sensors.map(s => s.id === sensorId ? data.result : s),
        selectedSensor: state.selectedSensor?.id === sensorId ? data.result : state.selectedSensor,
        isLoading: false 
      }));

      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to update sensor', isLoading: false });
      } else {
        set({ error: 'Failed to update sensor', isLoading: false });
      }
      return null;
    }
  },

  deleteSensor: async (sensorId) => {
    set({ isLoading: true, error: null });
    try {
      await deleteSensor(sensorId);
      
      set((state) => ({ 
        sensors: state.sensors.filter(s => s.id !== sensorId),
        selectedSensor: state.selectedSensor?.id === sensorId ? null : state.selectedSensor,
        isLoading: false 
      }));

      return true;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to delete sensor', isLoading: false });
      } else {
        set({ error: 'Failed to delete sensor', isLoading: false });
      }
      return false;
    }
  },

  getSensorReadings: async (sensorId, params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getSensorReadings(sensorId, params);
      const data = response as GenericResponse<SensorReading[]>;
      set({ isLoading: false });
      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get sensor readings', isLoading: false });
      } else {
        set({ error: 'Failed to get sensor readings', isLoading: false });
      }
      return null;
    }
  },

  submitSensorReading: async (sensorId, reading) => {
    set({ isLoading: true, error: null });
    try {
      await submitSensorReading(sensorId, reading);
      
      set((state) => ({
        sensors: state.sensors.map(sensor => {
          if (sensor.id === sensorId) {
            return {
              ...sensor,
              last_reading: {
                timestamp: reading.timestamp,
                value: reading.value,
                unit: reading.unit
              }
            };
          }
          return sensor;
        }),
        selectedSensor: state.selectedSensor?.id === sensorId 
          ? { 
              ...state.selectedSensor, 
              last_reading: {
                timestamp: reading.timestamp,
                value: reading.value,
                unit: reading.unit
              }
            }
          : state.selectedSensor,
        isLoading: false
      }));

      return true;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to submit sensor reading', isLoading: false });
      } else {
        set({ error: 'Failed to submit sensor reading', isLoading: false });
      }
      return false;
    }
  },

  calibrateSensor: async (sensorId) => {
    set({ isLoading: true, error: null });
    try {
      await calibrateSensor(sensorId);
      set({ isLoading: false });
      return true;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to calibrate sensor', isLoading: false });
      } else {
        set({ error: 'Failed to calibrate sensor', isLoading: false });
      }
      return false;
    }
  },

  getSensorHealth: async (sensorId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getSensorHealth(sensorId);
      const data = response as GenericResponse<{ status: string; details: any }>;
      set({ isLoading: false });
      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get sensor health', isLoading: false });
      } else {
        set({ error: 'Failed to get sensor health', isLoading: false });
      }
      return null;
    }
  },

  getSensorBatteryStatus: async (sensorId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getSensorBatteryStatus(sensorId);
      const data = response as GenericResponse<{ level: number; status: string }>;
      set({ isLoading: false });
      return data.result;
    } catch (error) {
      if (error instanceof AxiosError) {
        set({ error: error.response?.data?.["detail"] || 'Failed to get sensor battery status', isLoading: false });
      } else {
        set({ error: 'Failed to get sensor battery status', isLoading: false });
      }
      return null;
    }
  },
})); 